/**
 * PAPPO — Parent Service
 * Gestão de filhos pelo responsável (/api/parent).
 */

import { apiGet, apiPost, apiPut, apiDelete } from './api';
import type { Conversation, Message } from '../types/message';
import type { Spouse } from '../types/kyc';

export interface Child {
  id: number;
  username: string;
  display_name: string;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  /** Permissão por-filho (F5): pode gerar convites. Padrão desligado. */
  can_invite?: boolean;
  /** Controle de acesso por-filho (Painel de Controle): */
  frozen?: boolean; // congelamento "hora do jantar"
  schedule_enabled?: boolean; // janela de horário liberado ligada
  allowed_start?: string; // "HH:MM"
  allowed_end?: string; // "HH:MM"
  /** Humor de hoje do filho ("Como você tá?"), ou null se não marcou. */
  mood_today?: string | null;
  /** True = este responsável foi adicionado como co-responsável (guarda
   *  compartilhada); False/ausente = responsável original ("meu filho"). */
  is_shared?: boolean;
}

export interface ConnectionRequest {
  id: number;
  inviter_child_id: number;
  invitee_child_id: number;
  /** Quem iniciou o pedido; usado só para o rótulo de direção no Painel. */
  initiator_id: number | null;
  inviter_name: string;
  invitee_name: string;
  created_at: string;
}

/** Lista as crianças vinculadas ao responsável */
export async function listChildren() {
  return apiGet<{ children: Child[] }>('/parent/children');
}

/** Cria a conta de uma criança e vincula ao responsável */
export async function createChild(payload: {
  display_name: string;
  username: string;
  password: string;
}) {
  return apiPost<{ child: Child }>('/parent/children', payload);
}

/** Edita os dados do filho (nome e/ou senha). Só o responsável vinculado. */
export async function updateChild(
  childId: number,
  payload: { display_name?: string; password?: string }
) {
  return apiPut<{ child: Child }>(`/parent/children/${childId}`, payload);
}

/** Devolve o cônjuge vinculado (slot único) ou null */
export async function getSpouse() {
  return apiGet<{ spouse: Spouse | null }>('/parent/spouse');
}

/** Cria a conta ADULTA do cônjuge (limite 1). Cônjuge é adulto comum. */
export async function createSpouse(payload: {
  display_name: string;
  email: string;
  phone: string;
  password: string;
  username?: string;
}) {
  return apiPost<{ spouse: Spouse }>('/parent/spouse', payload);
}

/** Pedidos de ligação que aguardam aprovação deste responsável */
export async function listConnectionRequests() {
  return apiGet<{ requests: ConnectionRequest[] }>('/parent/connection-requests');
}

export async function approveConnection(id: number) {
  return apiPost(`/parent/connection-requests/${id}/approve`);
}

export async function rejectConnection(id: number) {
  return apiPost(`/parent/connection-requests/${id}/reject`);
}

/** Supervisão: lista as conversas de um filho vinculado (somente leitura) */
export async function getChildConversations(childId: number) {
  return apiGet<{ conversations: Conversation[] }>(
    `/parent/children/${childId}/conversations`
  );
}

/** Supervisão: mensagens de uma conversa de um filho (somente leitura) */
export async function getChildMessages(childId: number, conversationId: number) {
  return apiGet<{ messages: Message[] }>(
    `/parent/children/${childId}/conversations/${conversationId}/messages`
  );
}

/** Botão liga/desliga: exigir a minha aprovação para ligações dos meus filhos */
export async function getParentSettings() {
  return apiGet<{ approval_required: boolean }>('/parent/settings');
}

export async function setParentSettings(approval_required: boolean) {
  return apiPut<{ approval_required: boolean }>('/parent/settings', {
    approval_required,
  });
}

/** Permissão por-filho (F5): liga/desliga se o filho pode gerar convites. */
export async function setChildPermission(childId: number, can_invite: boolean) {
  return apiPut<{ can_invite: boolean }>(
    `/parent/children/${childId}/permissions`,
    { can_invite }
  );
}

/** Congela/descongela o chat do filho ("hora do jantar"). Efeito imediato. */
export async function setChildFreeze(childId: number, frozen: boolean) {
  return apiPut<{ frozen: boolean }>(`/parent/children/${childId}/freeze`, {
    frozen,
  });
}

/** Define os horários de acesso do filho (janela HH:MM). */
export async function setChildSchedule(
  childId: number,
  payload: { enabled: boolean; start: string; end: string }
) {
  return apiPut<{
    schedule_enabled: boolean;
    allowed_start: string;
    allowed_end: string;
  }>(`/parent/children/${childId}/schedule`, payload);
}

/** Preferências de aviso por IA (por-filho): de quais categorias de risco o
 *  responsável quer ser avisado. Opt-out: ausente/sem registro = recebe tudo.
 *  Aliciamento e automutilação NÃO aparecem aqui — são sempre ativos (trava de
 *  segurança). Chaves = valores em inglês que chegam no fan-out. */
export interface AlertPreferences {
  drugs: boolean;
  violence: boolean;
  bullying: boolean;
  scam: boolean;
  sexual_content: boolean;
}

/** Lê as preferências de aviso do responsável para um filho. */
export async function getAlertPreferences(childId: number) {
  return apiGet<{ preferences: AlertPreferences }>(
    `/parent/children/${childId}/alert-preferences`
  );
}

/** Atualiza (substitui) as preferências de aviso do responsável para um filho. */
export async function setAlertPreferences(
  childId: number,
  preferences: AlertPreferences
) {
  return apiPut<{ preferences: AlertPreferences }>(
    `/parent/children/${childId}/alert-preferences`,
    preferences
  );
}

// ── Ligação criança↔criança mediada pelos pais ("Conectar nossos filhos") ──
export interface ChildLinkIncoming {
  id: number;
  from_child_id: number;
  from_child_name: string;
  from_parent_name: string;
}
export interface ChildLinkOutgoing {
  id: number;
  from_child_name: string;
  to_parent_name: string;
  status: string;
}
export interface ChildLinkActive {
  id: number;
  meu_filho: string;
  meu_filho_id: number;
  amigo: string;
}
export interface ChildLinks {
  incoming: ChildLinkIncoming[];
  outgoing: ChildLinkOutgoing[];
  active: ChildLinkActive[];
}

/** Pedidos recebidos (aprovar), enviados (status) e ligações ativas (revogar). */
export async function listChildLinks() {
  return apiGet<ChildLinks>('/parent/child-links');
}

/** Paix inicia: conectar o filho dele com um filho de outro responsável. */
export async function createChildLink(from_child_id: number, to_parent_id: number) {
  return apiPost('/parent/child-links', { from_child_id, to_parent_id });
}

/** Paiy aprova, escolhendo o próprio filho. */
export async function approveChildLink(id: number, to_child_id: number) {
  return apiPost(`/parent/child-links/${id}/approve`, { to_child_id });
}

export async function rejectChildLink(id: number) {
  return apiPost(`/parent/child-links/${id}/reject`);
}

/** Corta uma ligação ativa (congela a conversa). */
export async function revokeChildLink(id: number) {
  return apiPost(`/parent/child-links/${id}/revoke`);
}

// ── Co-responsável (cônjuge / guarda compartilhada) ──
export interface CoGuardianIncoming {
  id: number;
  child_id: number;
  child_name: string;
  from_parent_name: string;
}
export interface CoGuardianOutgoing {
  id: number;
  child_name: string;
  to_parent_name: string;
}
export interface Guardian {
  id: number;
  name: string;
  is_me: boolean;
  removable: boolean;
}
export interface ChildGuardians {
  child_id: number;
  child_name: string;
  guardians: Guardian[];
}
export interface CoGuardiansData {
  incoming: CoGuardianIncoming[];
  outgoing: CoGuardianOutgoing[];
  children: ChildGuardians[];
}

/** Convites recebidos/enviados + responsáveis por filho. */
export async function listCoGuardians() {
  return apiGet<CoGuardiansData>('/parent/co-guardians');
}

/** Convida um adulto (por email) para co-responsável de um filho. */
export async function inviteCoGuardian(childId: number, email: string) {
  return apiPost(`/parent/children/${childId}/co-guardians`, { email });
}

export async function acceptCoGuardian(id: number) {
  return apiPost(`/parent/co-guardians/${id}/accept`);
}

export async function rejectCoGuardian(id: number) {
  return apiPost(`/parent/co-guardians/${id}/reject`);
}

/** Remove um co-responsável (ou o próprio sai). */
export async function removeCoGuardian(childId: number, targetId: number) {
  return apiDelete(`/parent/children/${childId}/co-guardians/${targetId}`);
}

